import type { ComputedRef } from "vue";
import type { IndividualCableCalculations } from "../calculationTypes";
import { computed } from "vue";
import {
  getCableData,
  getTemperatureAtCableLengthDownholeF,
  calculateEllipseMaxDepth,
  isSurfaceCable
} from "@/libs/calculations/cables";
import { SURFACE_CABLE } from "@/libs/calculations/constants";
import type { CablesCtx } from "./context";

// Cable and MLE calcs.
export const defineCables = (ctx: CablesCtx) => {
  const {
    _equipment,
    _parameters,
    currentCables,
    tempGrad,
    lengthForMleFt,
    cableSurfaceLengthFt,
    surfaceTemperatureF,
    pumpIntakeTemperatureF,
    pumpData,
    motorData,
    reratedNameplateA
  } = ctx;

  const orderedCables = computed(() => {
    const result: typeof currentCables.value = [];

    // 'Splice' surface cable from top cable
    if (currentCables.value.length > 0) {
      if (
        cableSurfaceLengthFt.value > 0 &&
        cableSurfaceLengthFt.value <= currentCables.value[0].length_feet + 1e-6
      ) {
        // Create surface cable
        const surfaceCable = {
          ...currentCables.value[0],
          length_feet: Math.min(
            cableSurfaceLengthFt.value,
            currentCables.value[0].length_feet
          ),
          id: SURFACE_CABLE
        };
        result.push(surfaceCable);

        // Adjust the length of the first cable
        const adjustedFirstCable = {
          ...currentCables.value[0],
          length_feet:
            currentCables.value[0].length_feet - surfaceCable.length_feet
        };
        if (adjustedFirstCable.length_feet > 0) result.push(adjustedFirstCable);
      } else {
        // If no surface cable is needed or if it's longer than the first cable, just add the first cable as is
        result.push(currentCables.value[0]);
      }

      // Add the rest of the cables
      result.push(...currentCables.value.slice(1));
    }

    return result;
  });

  const getCableCalculations = (
    orderedCables: ComputedRef<typeof currentCables.value>,
    frequencyHz: number,
    motorCurrent: number,
    tempGrad: number,
    surfaceTemperatureF: number,
    cableCostKWHrUSD: number
  ) => {
    return orderedCables.value.reduce<IndividualCableCalculations>(
      (acc, xrefCable) => {
        // Determine if this is a surface cable
        const isSurface = isSurfaceCable(xrefCable.id);

        // Initialize start and end lengths
        let downholeStartCableLengthFt = 0;
        let downholeEndCableLengthFt = 0;

        // For downhole cables, we need to calculate cumulative lengths
        if (!isSurface) {
          // Find this cable's index among downhole cables
          const downholeIndex = orderedCables.value
            .filter(cable => !isSurfaceCable(cable.id))
            .findIndex(cable => cable.id === xrefCable.id);

          if (downholeIndex === 0) {
            // First downhole cable starts at 0
            downholeStartCableLengthFt = 0;
            // End is just its own length
            downholeEndCableLengthFt = xrefCable.length_feet || 0;
          } else {
            // Calculate start depth (sum of all previous downhole cables)
            downholeStartCableLengthFt = orderedCables.value
              .filter(cable => !isSurfaceCable(cable.id))
              .slice(0, downholeIndex)
              .reduce((sum, cable) => sum + (cable.length_feet || 0), 0);

            // Calculate end depth (start + own length)
            downholeEndCableLengthFt =
              downholeStartCableLengthFt + (xrefCable.length_feet || 0);
          }
        }

        // Calculate cable temperatures based on depths
        const cableStartTempF = isSurface
          ? 59 // @TODO: Maybe switch with surfaceTemperatureF.value
          : getTemperatureAtCableLengthDownholeF(
              downholeStartCableLengthFt,
              tempGrad,
              surfaceTemperatureF
            );

        const cableEndTempF = isSurface
          ? 59 // @TODO: Maybe switch with surfaceTemperatureF.value
          : getTemperatureAtCableLengthDownholeF(
              downholeEndCableLengthFt,
              tempGrad,
              surfaceTemperatureF
            );

        const cableMaxDepth = calculateEllipseMaxDepth(
          xrefCable?.r_cable?.width_inches,
          xrefCable?.r_cable?.depth_inches
        );

        acc[xrefCable.id] = {
          ...getCableData(
            xrefCable?.r_cable,
            xrefCable.length_feet,
            frequencyHz,
            motorCurrent,
            cableStartTempF,
            cableEndTempF,
            cableCostKWHrUSD!,
            reratedNameplateA.value,
            isSurface
          ),
          xrefCable,
          downholeStartCableLengthFt,
          downholeEndCableLengthFt,
          cableMaxDepth,
          isSurface
        };

        return acc;
      },
      {}
    );
  };

  const individualCableCalculations = computed(() => {
    return getCableCalculations(
      orderedCables,
      _parameters.value?.pump_hz, // Use the configured pump_hz
      motorData.value.motor_current,
      tempGrad.value,
      surfaceTemperatureF.value,
      _parameters.value?.cable_cost_kw_hr_usd as number
    );
  });

  // Motor current here is defaulted at 60Hz when calling getCableData
  const individualCableCalculations60Hz = computed(() => {
    return getCableCalculations(
      orderedCables,
      60, // Always use 60Hz for this calculation
      motorData.value.motor_current,
      tempGrad.value,
      surfaceTemperatureF.value,
      _parameters.value?.cable_cost_kw_hr_usd as number
    );
  });

  // Motor current here is defaulted at 60Hz when calling getCableData
  const individualCableCalculationsMaxHz = computed(() => {
    return getCableCalculations(
      orderedCables,
      ctx.surfaceDriveMaxHz.value, // Use hax hz for this calculation
      motorData.value.motor_current,
      tempGrad.value,
      surfaceTemperatureF.value,
      _parameters.value?.cable_cost_kw_hr_usd as number
    );
  });

  const totalImpedance = computed(() =>
    orderedCables.value.reduce(
      (impedance, cable) =>
        (impedance +=
          individualCableCalculations.value[cable.id]?.cableReactance || 0),
      0
    )
  );

  const downholeTotalImpedance = computed(() =>
    orderedCables.value
      .filter(cable => !isSurfaceCable(cable.id))
      .reduce(
        (impedance, cable) =>
          (impedance +=
            individualCableCalculations.value[cable.id]?.cableReactance || 0),
        0
      )
  );

  const totalCablesAverageTemperatureF = computed(
    () =>
      orderedCables.value.reduce(
        (impedance, cable) =>
          (impedance +=
            individualCableCalculations.value[cable.id]?.cableMaxTemperatureF ||
            0),
        0
      ) / orderedCables.value.length
  );

  const downholeTotalCablesAverageTemperatureF = computed(() => {
    // Maybe make this a top level computed as well?
    const downholeCables = orderedCables.value.filter(
      cable => !isSurfaceCable(cable.id)
    );
    return downholeCables.length > 0
      ? downholeCables.reduce(
          (temp, cable) =>
            (temp +=
              individualCableCalculations.value[cable.id]
                ?.cableMaxTemperatureF || 0),
          0
        ) / downholeCables.length
      : 0;
  });

  const totalCablesCostPerMonthUSD = computed(() =>
    orderedCables.value.reduce(
      (costSum, cable) =>
        costSum +
          individualCableCalculations.value[cable.id]
            ?.cablePowerCostPerMonthUSD || 0,
      0
    )
  );

  const totalCablesVoltLossV = computed(
    () =>
      orderedCables.value.reduce(
        (voltageLoss, cable) =>
          voltageLoss +
            individualCableCalculations.value[cable.id]?.totalVoltLossV || 0,
        0
      ) + mleCableData.value.totalVoltLossV
  );

  // This is voltage loss at nameplate voltage and at max hz
  const totalCablesVoltLossAtNameplateAtMaxHzV = computed(
    () =>
      orderedCables.value.reduce(
        (voltageLoss, cable) =>
          voltageLoss +
            individualCableCalculationsMaxHz.value[cable.id]
              ?.totalVoltLossAtNameplateV || 0,
        0
      ) + mleCableData.value.totalVoltLossAtNameplateV
  );

  const totalCablesPowerLossKW = computed(() =>
    orderedCables.value.reduce(
      (powerLossKW, cable) =>
        powerLossKW +
          individualCableCalculations.value[cable.id]?.powerLossKW || 0,
      0
    )
  );

  const totalCablesLengthFt = computed(() =>
    orderedCables.value.reduce(
      (lengthFt, cable) => lengthFt + (cable.length_feet || 0),
      0
    )
  );

  const downholeTotalCablesLengthFt = computed(() =>
    orderedCables.value
      .filter(cable => !isSurfaceCable(cable.id))
      .reduce((lengthFt, cable) => lengthFt + (cable.length_feet || 0), 0)
  );

  const totalCablesWeightPounds = computed(() =>
    orderedCables.value.reduce(
      (weightPounds, cable) =>
        weightPounds +
          individualCableCalculations.value[cable?.id]?.cableWeightLbs || 0,
      0
    )
  );

  const downholeTotalCablesWeightPounds = computed(() =>
    orderedCables.value
      .filter(cable => !isSurfaceCable(cable.id))
      .reduce(
        (weightPounds, cable) =>
          weightPounds +
            individualCableCalculations.value[cable?.id]?.cableWeightLbs || 0,
        0
      )
  );

  const totalCablesVoltLossV60Hz = computed(() =>
    orderedCables.value.reduce(
      (voltageLoss, cable) =>
        voltageLoss +
          individualCableCalculations60Hz.value[cable.id]?.totalVoltLossV || 0,
      0
    )
  );

  // Instead of using temps at MLE start/end, industry uses
  // pump intake temp and pump discharge temp to take pump temp into
  // consideration since MLE is mounted against it
  const mleCableData = computed(() => {
    // Calculate MLE data
    const mleStartMD = lengthForMleFt.value;
    const mleEndMD = mleStartMD + (_equipment.value?.r_mle?.length_feet || 0);
    const mleEllipseMaxDepth = calculateEllipseMaxDepth(
      _equipment.value?.r_mle?.width_inches,
      _equipment.value?.r_mle?.depth_inches
    );

    return {
      ...getCableData(
        _equipment.value?.r_mle,
        _equipment.value?.r_mle?.length_feet,
        _parameters.value?.pump_hz,
        motorData.value.motor_current,
        pumpIntakeTemperatureF.value,
        pumpData.value.dischargeTemperatureF || 0,
        _parameters.value?.cable_cost_kw_hr_usd as number,
        reratedNameplateA.value
      ),
      mleStartMD,
      mleEndMD,
      mleEllipseMaxDepth
    };
  });

  return {
    orderedCables,
    getCableCalculations,
    individualCableCalculations,
    individualCableCalculations60Hz,
    individualCableCalculationsMaxHz,
    totalImpedance,
    downholeTotalImpedance,
    totalCablesAverageTemperatureF,
    downholeTotalCablesAverageTemperatureF,
    totalCablesCostPerMonthUSD,
    totalCablesVoltLossV,
    totalCablesVoltLossAtNameplateAtMaxHzV,
    totalCablesPowerLossKW,
    totalCablesLengthFt,
    downholeTotalCablesLengthFt,
    totalCablesWeightPounds,
    downholeTotalCablesWeightPounds,
    totalCablesVoltLossV60Hz,
    mleCableData
  };
};

export type CablesCalcs = ReturnType<typeof defineCables>;
